import { MatchupConfig, ChatInput, ChatResponse, InsightItem, InsightsResponse, Stage } from '../types';
import { formatStageName } from '../utils/stageUtils';
import { apiEventSourceUrl, apiFetch, API_BASE_URL } from '../utils/httpClient';
// New API Types based on OpenAPI spec
// TeamStatsRequest interface removed - now using direct parameters for GET endpoint

// PlayersStatsRequest interface removed - now using direct parameters for GET endpoint

// LineupsStatsRequest interface removed - now using query parameters directly

export interface RecommendationItem {
  focus_area: string;
  supporting_data: string;
  why_it_matters: string;
  coaching_suggestion: string;
}

export interface RecommendationResponse {
  recommendations: RecommendationItem[];
  teamId: string;
  oppTeamId: string;
}

// InsightsResponse is now imported from types/index.ts

export interface ValidationError {
  loc: (string | number)[];
  msg: string;
  type: string;
}

export interface HTTPValidationError {
  detail: ValidationError[];
}

// Legacy types for backward compatibility
export interface TeamStatsResponse {
  success: boolean;
  team_name: string;
  season: string;
  season_type: string;
  data: any; // Raw team stats data from MongoDB
}

export interface SplitTypesResponse {
  success: boolean;
  team_name: string;
  season: string;
  season_type: string;
  split_types: string[];
}

export interface ApiResponse {
  response?: string;
  error?: string;
  user_message?: string;
}

// InsightItem is now imported from types/index.ts

export interface RecommendationsReport {
  report: {
    insight: InsightItem[];
  };
  teamId: string;
}

export interface ReportResponse {
  report: {
    id: string;
    title: string;
    content: string;
    generatedAt: string;
    matchup: {
      team1: string;
      team2: string;
    };
    sections: {
      keyMatchups: string;
      recentForm: string;
      lineupStrategy: string;
      defensiveFocus: string;
      winFactors: string;
      advancedStats: string;
    };
  };
  error?: string;
}

class ApiService {
  private baseUrl: string;
  private pendingRequests: Map<string, Promise<any>> = new Map();

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * Create a unique key for request deduplication
   */
  private createRequestKey(method: string, url: string): string {
    return `${method}:${url}`;
  }

  /**
   * Execute a request with deduplication to prevent duplicate calls
   */
  private async executeRequest<T>(method: string, url: string, requestFn: () => Promise<T>): Promise<T> {
    const key = this.createRequestKey(method, url);
    
    // Check if request is already in progress
    if (this.pendingRequests.has(key)) {
      console.log(`🔄 Request deduplication: Reusing existing request for ${key}`);
      return this.pendingRequests.get(key) as Promise<T>;
    }
    
    // Create new request
    const requestPromise = requestFn().finally(() => {
      // Clean up completed request from cache
      this.pendingRequests.delete(key);
    });
    
    // Store in pending requests
    this.pendingRequests.set(key, requestPromise);
    
    return requestPromise;
  }

  /**
   * Convert frontend season format (2025-26) to backend format (2025-2026)
   */
  private convertSeasonToBackendFormat(season: string): string {
    // If season is in YYYY-YY format, convert to YYYY-YYYY
    if (season.includes('-')) {
      const [startYear, endYear] = season.split('-');
      // Check if endYear is already 4 digits
      if (endYear.length === 4) {
        return season; // Already in YYYY-YYYY format
      }
      const fullEndYear = `20${endYear}`;
      return `${startYear}-${fullEndYear}`;
    }
    // If no dash, return as-is
    return season;
  }

  // League endpoints
  async getLeagues(): Promise<any[]> {
    const response = await apiFetch(`${this.baseUrl}/api/v1/leagues`);
    
    if (!response.ok) {
      throw new Error(`Failed to get leagues: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  async getStages(league: string): Promise<Stage[]> {
    const response = await apiFetch(`${this.baseUrl}/api/v1/leagues/${league}/stages`);
    if (!response.ok) {
      throw new Error(`Failed to get stages for league ${league}: ${response.status} ${response.statusText}`);
    }
    
    const stages = await response.json();
    // Backend already returns Stage objects with stageId and stageName
    // Handle both object format (from backend) and string format (defensive)
    return stages.map((stage: Stage | string) => {
      if (typeof stage === 'string') {
        return {
          stageId: stage,
          stageName: formatStageName(stage)
        };
      }
      // Backend returns objects with stageId and stageName
      return stage as Stage;
    });
  }

  // New Teams endpoints
  async getTeams(): Promise<any[]> {
    const response = await apiFetch(`${this.baseUrl}/api/v1/teams/`);
    
    if (!response.ok) {
      throw new Error(`Failed to get teams: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  async getTeamsByLeague(league: string): Promise<any[]> {
    const params = new URLSearchParams({
      league: league
    });
    
    const response = await apiFetch(`${this.baseUrl}/api/v1/teams?${params}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get teams for league ${league}: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  async getTeamMetadata(teamId: string): Promise<any> {
    const response = await apiFetch(`${this.baseUrl}/api/v1/teams/${teamId}/metadata`);
    
    if (!response.ok) {
      throw new Error(`Failed to get team metadata for ${teamId}: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  async getTeamStats(league: string, teamId: string, seasonId: string, stage: string, splitKeys?: string[]): Promise<any> {
    const params = new URLSearchParams({
      seasonId: seasonId,
      stage: stage
    });
    
    // Always include 'overall' split along with any user-selected splits
    const splitsToSend = ['overall'];
    if (splitKeys && splitKeys.length > 0) {
      splitsToSend.push(...splitKeys);
    }
    
    splitsToSend.forEach(split => {
      params.append('splits', split);
    });
    
    const response = await apiFetch(`${this.baseUrl}/api/v1/teams/${league}/${teamId}/stats?${params}`, {
      method: 'GET',
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get team stats for ${teamId}: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  async getTeamRecommendations(teamId: string, opponentTeamId: string, stage: string): Promise<RecommendationResponse> {
    // Validate required parameters
    if (!teamId || teamId === 'undefined' || teamId === 'null') {
      throw new Error('Team ID is required for team recommendations');
    }
    if (!opponentTeamId || opponentTeamId === 'undefined' || opponentTeamId === 'null') {
      throw new Error('Opponent Team ID is required for team recommendations');
    }
    if (!stage || stage === 'undefined' || stage === 'null') {
      throw new Error('Stage is required for team recommendations');
    }
    
    const params = new URLSearchParams({
      team_id: teamId,
      opponent_team_id: opponentTeamId,
      stage: stage
    });
    
    const response = await apiFetch(`${this.baseUrl}/api/v1/teams/recommendations?${params}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get team recommendations: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  // New Players endpoints
  async getPlayers(teamId: string, seasonId: string): Promise<any[]> {
    const response = await apiFetch(`${this.baseUrl}/api/v1/players/${teamId}/${seasonId}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get players for team ${teamId}: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  async getPlayerMetadata(playerId: string): Promise<any> {
    const response = await apiFetch(`${this.baseUrl}/api/v1/players/${playerId}/metadata`);
    
    if (!response.ok) {
      throw new Error(`Failed to get player metadata for ${playerId}: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  async getPlayerStats(league: string, teamId: string, seasonId: string, stage: string, split: string): Promise<any> {
    console.log('🔍 API Service getPlayerStats - Parameters:', { league, teamId, seasonId, stage, split });
    
    const params = new URLSearchParams({
      seasonId: seasonId,
      stage: stage,
      split: split
    });
    
    const url = `${this.baseUrl}/api/v1/players/${league}/${teamId}/stats?${params}`;
    
    return this.executeRequest('GET', url, async () => {
      const response = await apiFetch(url, {
        method: 'GET',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get player stats for ${teamId}: ${response.status} ${response.statusText}`);
      }
      
      return response.json();
    });
  }

  async getLineupsStats(leagueId: string, teamId: string, seasonId: string, stage: string, size: number = 5, splitKeys?: string[]): Promise<any> {
    const params = new URLSearchParams({
      leagueId: leagueId,
      teamId: teamId,
      seasonId: seasonId,
      stage: stage,
      size: size.toString()
    });
    
    // Always include 'overall' split along with any user-selected splits
    const splitsToSend = ['overall'];
    if (splitKeys && splitKeys.length > 0) {
      splitsToSend.push(...splitKeys);
    }
    
    splitsToSend.forEach(split => {
      params.append('splits', split);
    });
    
    const url = `${this.baseUrl}/api/v1/lineups/stats?${params}`;
    console.log('🔍 getLineupsStats - Full URL:', url);
    console.log('🔍 getLineupsStats - Parameters:', { leagueId, teamId, seasonId, stage, size, splitKeys });
    
    const response = await apiFetch(url, {
      method: 'GET',
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get lineups stats for ${teamId}: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  // Splits endpoints
  async getTeamSplits(league: string): Promise<string[]> {
    const params = new URLSearchParams({
      league: league
    });
    
    const response = await apiFetch(`${this.baseUrl}/api/v1/teams/splits?${params}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get team splits: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  async getPlayersSplits(league: string): Promise<string[]> {
    const params = new URLSearchParams({
      league: league
    });
    
    const response = await apiFetch(`${this.baseUrl}/api/v1/players/splits?${params}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get players splits: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  async getLineupsSplits(league: string): Promise<string[]> {
    const params = new URLSearchParams({
      league: league
    });
    
    const response = await apiFetch(`${this.baseUrl}/api/v1/lineups/splits?${params}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get lineups splits: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  // New Insights endpoints
  async getTeamInsights(teamId: string, seasonId: string, stage: string, splitKeys?: string[], league?: string, forceRefresh?: boolean): Promise<InsightsResponse> {
    // Validate required parameters
    if (!teamId || teamId === 'undefined' || teamId === 'null') {
      throw new Error('Team ID is required for team insights');
    }
    if (!seasonId || seasonId === 'undefined' || seasonId === 'null') {
      throw new Error('Season ID is required for team insights');
    }
    if (!stage || stage === 'undefined' || stage === 'null') {
      throw new Error('Stage is required for team insights');
    }
    if (!league || league === 'undefined' || league === 'null') {
      throw new Error('League is required for team insights. Please configure your league in settings.');
    }
    
    const params = new URLSearchParams();
    
    // Add league parameter
    params.append('league', league);
    
    // Add force_refresh parameter if provided
    if (forceRefresh) {
      params.append('force_refresh', 'true');
    }
    
    // Always include 'overall' split along with any user-selected splits
    const splitsToSend = ['overall'];
    if (splitKeys && splitKeys.length > 0) {
      splitsToSend.push(...splitKeys);
    }
    
    splitsToSend.forEach(split => {
      params.append('splits', split);
    });
    
    const url = `${this.baseUrl}/api/v1/insights/${teamId}/${seasonId}/${stage}?${params}`;
    
    const response = await apiFetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to get team insights: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  async getPlayersInsights(teamId: string, seasonId: string, stage: string, splitKeys?: string[], league?: string, forceRefresh?: boolean): Promise<InsightsResponse> {
    // Validate required parameters
    if (!teamId || teamId === 'undefined' || teamId === 'null') {
      throw new Error('Team ID is required for players insights');
    }
    if (!seasonId || seasonId === 'undefined' || seasonId === 'null') {
      throw new Error('Season ID is required for players insights');
    }
    if (!stage || stage === 'undefined' || stage === 'null') {
      throw new Error('Stage is required for players insights');
    }
    if (!league || league === 'undefined' || league === 'null') {
      throw new Error('League is required for players insights. Please configure your league in settings.');
    }
    
    const params = new URLSearchParams();
    
    // Add league parameter
    params.append('league', league);
    
    // Add force_refresh parameter if provided
    if (forceRefresh) {
      params.append('force_refresh', 'true');
    }
    
    // Always include 'overall' split along with any user-selected splits
    const splitsToSend = ['overall'];
    if (splitKeys && splitKeys.length > 0) {
      splitsToSend.push(...splitKeys);
    }
    
    splitsToSend.forEach(split => {
      params.append('splits', split);
    });
    
    const url = `${this.baseUrl}/api/v1/insights/${teamId}/${seasonId}/${stage}/players?${params}`;
    
    const response = await apiFetch(url);
    
    if (!response.ok) {
      throw new Error(`Failed to get players insights: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  async getLineupsInsights(teamId: string, seasonId: string, stage: string, size: number = 5, splitKeys?: string[], league?: string, forceRefresh?: boolean): Promise<InsightsResponse> {
    // Validate required parameters
    if (!teamId || teamId === 'undefined' || teamId === 'null') {
      throw new Error('Team ID is required for lineups insights');
    }
    if (!seasonId || seasonId === 'undefined' || seasonId === 'null') {
      throw new Error('Season ID is required for lineups insights');
    }
    if (!stage || stage === 'undefined' || stage === 'null') {
      throw new Error('Stage is required for lineups insights');
    }
    if (!league || league === 'undefined' || league === 'null') {
      throw new Error('League is required for lineups insights. Please configure your league in settings.');
    }
    
    const params = new URLSearchParams({
      size: size.toString()
    });
    
    // Add league parameter
    params.append('league', league);
    
    // Add force_refresh parameter if provided
    if (forceRefresh) {
      params.append('force_refresh', 'true');
    }
    
    // Always include 'overall' split along with any user-selected splits
    const splitsToSend = ['overall'];
    if (splitKeys && splitKeys.length > 0) {
      splitsToSend.push(...splitKeys);
    }
    
    splitsToSend.forEach(split => {
      params.append('splits', split);
    });
    
    const response = await apiFetch(`${this.baseUrl}/api/v1/insights/${teamId}/${seasonId}/${stage}/lineups?${params}`);

    if (!response.ok) {
      throw new Error(`Failed to get lineups insights: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  // Refresh insights endpoints
  async refreshTeamInsights(
    league: string,
    insightIds: string[],
    splits: string[],
    matchConfig: {
      stage: string;
      userTeam: { teamId: string; teamName: string };
      opponentTeam: { teamId: string; teamName: string };
      seasonId: string;
      league: string;
    }
  ): Promise<InsightsResponse> {
    const url = `${this.baseUrl}/api/v1/insights/${league}/teams/refresh`;
    
    const requestBody = {
      insights_ids: insightIds,
      splits: splits,
      match_config: matchConfig
    };

    const response = await apiFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`Failed to refresh team insights: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  async refreshPlayerInsights(
    league: string,
    insightIds: string[],
    splits: string[],
    matchConfig: {
      stage: string;
      userTeam: { teamId: string; teamName: string };
      opponentTeam: { teamId: string; teamName: string };
      seasonId: string;
      league: string;
    }
  ): Promise<InsightsResponse> {
    const url = `${this.baseUrl}/api/v1/insights/${league}/players/refresh`;
    
    const requestBody = {
      insights_ids: insightIds,
      splits: splits,
      match_config: matchConfig
    };

    const response = await apiFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`Failed to refresh player insights: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  async refreshLineupInsights(
    league: string,
    insightIds: string[],
    splits: string[],
    matchConfig: {
      stage: string;
      userTeam: { teamId: string; teamName: string };
      opponentTeam: { teamId: string; teamName: string };
      seasonId: string;
      league: string;
    }
  ): Promise<InsightsResponse> {
    const url = `${this.baseUrl}/api/v1/insights/${league}/lineups/refresh`;
    
    const requestBody = {
      insights_ids: insightIds,
      splits: splits,
      match_config: matchConfig
    };

    const response = await apiFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`Failed to refresh lineup insights: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // Offensive recommendations endpoint
  async getOffensiveRecommendations(matchConfig: {
    stage: string;
    userTeam: { teamId: string; teamName: string };
    opponentTeam: { teamId: string; teamName: string };
    seasonId: string;
    league: string;
  }): Promise<RecommendationResponse> {
    if (!matchConfig.userTeam?.teamId || matchConfig.userTeam.teamId === 'undefined' || matchConfig.userTeam.teamId === 'null') {
      throw new Error('User Team ID is required for offensive recommendations');
    }
    if (!matchConfig.opponentTeam?.teamId || matchConfig.opponentTeam.teamId === 'undefined' || matchConfig.opponentTeam.teamId === 'null') {
      throw new Error('Opponent Team ID is required for offensive recommendations');
    }
    if (!matchConfig.seasonId || matchConfig.seasonId === 'undefined' || matchConfig.seasonId === 'null') {
      throw new Error('Season ID is required for offensive recommendations');
    }
    if (!matchConfig.stage || matchConfig.stage === 'undefined' || matchConfig.stage === 'null') {
      throw new Error('Stage is required for offensive recommendations');
    }
    if (!matchConfig.league || matchConfig.league === 'undefined' || matchConfig.league === 'null') {
      throw new Error('League is required for offensive recommendations');
    }
    
    const requestBody = {
      match_config: matchConfig
    };
    
    const url = `${this.baseUrl}/api/v1/recommendations/offensive`;
    
    const response = await apiFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get offensive recommendations: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  }

  // Defensive recommendations endpoint
  async getDefensiveRecommendations(matchConfig: {
    stage: string;
    userTeam: { teamId: string; teamName: string };
    opponentTeam: { teamId: string; teamName: string };
    seasonId: string;
    league: string;
  }): Promise<RecommendationResponse> {
    if (!matchConfig.userTeam?.teamId || matchConfig.userTeam.teamId === 'undefined' || matchConfig.userTeam.teamId === 'null') {
      throw new Error('User Team ID is required for defensive recommendations');
    }
    if (!matchConfig.opponentTeam?.teamId || matchConfig.opponentTeam.teamId === 'undefined' || matchConfig.opponentTeam.teamId === 'null') {
      throw new Error('Opponent Team ID is required for defensive recommendations');
    }
    if (!matchConfig.seasonId || matchConfig.seasonId === 'undefined' || matchConfig.seasonId === 'null') {
      throw new Error('Season ID is required for defensive recommendations');
    }
    if (!matchConfig.stage || matchConfig.stage === 'undefined' || matchConfig.stage === 'null') {
      throw new Error('Stage is required for defensive recommendations');
    }
    if (!matchConfig.league || matchConfig.league === 'undefined' || matchConfig.league === 'null') {
      throw new Error('League is required for defensive recommendations');
    }
    
    const requestBody = {
      match_config: matchConfig
    };
    
    const url = `${this.baseUrl}/api/v1/recommendations/defensive`;
    
    const response = await apiFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get defensive recommendations: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  }

  // Refresh offensive recommendation
  async refreshOffensiveRecommendation(
    matchConfig: {
      stage: string;
      userTeam: { teamId: string; teamName: string };
      opponentTeam: { teamId: string; teamName: string };
      seasonId: string;
      league: string;
    },
    splits: string[],
    userTeamInsightsIds: string[],
    opponentTeamInsightsIds: string[],
    rids: string[]
  ): Promise<RecommendationResponse> {
    const requestBody = {
      match_config: matchConfig,
      splits: splits,
      user_team_insights_ids: userTeamInsightsIds,
      opponent_team_insights_ids: opponentTeamInsightsIds,
      rids: rids
    };
    
    const url = `${this.baseUrl}/api/v1/recommendations/offensive/refresh`;
    console.log('🔍 API refreshOffensiveRecommendation - URL:', url);
    console.log('🔍 API refreshOffensiveRecommendation - Request body:', requestBody);
    
    const response = await apiFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to refresh offensive recommendation: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('🔍 API refreshOffensiveRecommendation - Response data:', data);
    return data;
  }

  // Refresh defensive recommendation
  async refreshDefensiveRecommendation(
    matchConfig: {
      stage: string;
      userTeam: { teamId: string; teamName: string };
      opponentTeam: { teamId: string; teamName: string };
      seasonId: string;
      league: string;
    },
    splits: string[],
    userTeamInsightsIds: string[],
    opponentTeamInsightsIds: string[],
    rids: string[]
  ): Promise<RecommendationResponse> {
    const requestBody = {
      match_config: matchConfig,
      splits: splits,
      user_team_insights_ids: userTeamInsightsIds,
      opponent_team_insights_ids: opponentTeamInsightsIds,
      rids: rids
    };
    
    const url = `${this.baseUrl}/api/v1/recommendations/defensive/refresh`;
    console.log('🔍 API refreshDefensiveRecommendation - URL:', url);
    console.log('🔍 API refreshDefensiveRecommendation - Request body:', requestBody);
    
    const response = await apiFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to refresh defensive recommendation: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('🔍 API refreshDefensiveRecommendation - Response data:', data);
    return data;
  }

  // Approve offensive recommendation
  async approveOffensiveRecommendation(rids: string[], league: string = "NBA"): Promise<RecommendationResponse> {
    const requestBody = {
      rids: rids,
      league: league
    };
    
    const url = `${this.baseUrl}/api/v1/recommendations/offensive/approve`;
    console.log('🔍 API approveOffensiveRecommendation - URL:', url);
    console.log('🔍 API approveOffensiveRecommendation - Request body:', requestBody);
    
    const response = await apiFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to approve offensive recommendation: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('🔍 API approveOffensiveRecommendation - Response data:', data);
    return data;
  }

  // Approve defensive recommendation
  async approveDefensiveRecommendation(rids: string[], league: string = "NBA"): Promise<RecommendationResponse> {
    const requestBody = {
      rids: rids,
      league: league
    };
    
    const url = `${this.baseUrl}/api/v1/recommendations/defensive/approve`;
    console.log('🔍 API approveDefensiveRecommendation - URL:', url);
    console.log('🔍 API approveDefensiveRecommendation - Request body:', requestBody);
    
    const response = await apiFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to approve defensive recommendation: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('🔍 API approveDefensiveRecommendation - Response data:', data);
    return data;
  }

  // Submit decline feedback for offensive recommendation
  async submitDeclineFeedback(
    recommendationId: string,
    feedback: string,
    recommendationType: 'offensive' | 'defensive',
    matchConfig: {
      stage: string;
      userTeam: { teamId: string; teamName: string };
      opponentTeam: { teamId: string; teamName: string };
      seasonId: string;
      league: string;
    }
  ): Promise<void> {
    const requestBody = {
      recommendation_id: recommendationId,
      feedback: feedback,
      recommendation_type: recommendationType,
      match_config: matchConfig
    };
    
    const url = `${this.baseUrl}/api/v1/recommendations/decline-feedback`;
    
    const response = await apiFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to submit decline feedback: ${response.status} ${response.statusText}`);
    }
    
    console.log('🔍 API submitDeclineFeedback - Feedback submitted successfully');
  }

  // Health check endpoint
  async healthCheck(): Promise<any> {
    const response = await apiFetch(`${this.baseUrl}/health`);
    
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status} ${response.statusText}`);
    }
    
    return response.json();
  }

  // Team configuration endpoints
  async setTeamConfig(config: MatchupConfig, league?: string): Promise<void> {
    const response = await apiFetch(`${this.baseUrl}/api/v1/teams/config`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_team_abbreviation: config.user_team.teamId,
        opponent_team_abbreviation: config.opponent_team.teamId,
        season: config.season,
        matchup_type: config.matchup_type,
        league: league || '',
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to set team configuration');
    }
  }

  async getTeamConfig(): Promise<{ config: any } | null> {
    try {
      const response = await apiFetch(`${this.baseUrl}/api/v1/teams/config`);
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to get team configuration:', error);
      return null;
    }
  }





  // Chat endpoints
  async sendChatMessage(chatInput: ChatInput): Promise<ChatResponse> {
    console.log('🔍 API Service sendChatMessage - ChatInput object:', chatInput);

    const response = await apiFetch(`${this.baseUrl}/api/v1/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(chatInput),
    });

    if (!response.ok) {
      throw new Error('Failed to send chat message');
    }

    return response.json();
  }

  async clearChat(): Promise<void> {
    const response = await apiFetch(`${this.baseUrl}/api/v1/chat/clear`, {
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error('Failed to clear chat');
    }
  }

  // Q&A streaming endpoint
  async hasQAMatch(question: string): Promise<boolean> {
    try {
      const url = `${this.baseUrl}/api/v1/qa/test?question=${encodeURIComponent(question)}`;
      const response = await apiFetch(url, { 
        method: 'POST',
      });
      if (!response.ok) {
        return false;
      }
      const data = await response.json();
      return Boolean(data?.match);
    } catch (e) {
      return false;
    }
  }

  async streamQAResponse(question: string): Promise<ReadableStream<Uint8Array> | null> {
    try {
      const response = await apiFetch(`${this.baseUrl}/api/v1/qa-stream/stream/${encodeURIComponent(question)}`, {
        method: 'GET',
        headers: {
          'Accept': 'text/event-stream',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          // No Q&A match found
          return null;
        }
        throw new Error('Failed to stream Q&A response');
      }

      return response.body;
    } catch (error) {
      console.error('Error streaming Q&A response:', error);
      return null;
    }
  }

  // Use production backend for EventSource SSE
  openQAEventSource(question: string): EventSource {
    const url = apiEventSourceUrl(`/api/v1/qa-stream/stream/${encodeURIComponent(question)}`);
    return new EventSource(url, { withCredentials: true });
  }

  // Report generation endpoints
  async generateReport(teamName: string, reportType: string = 'team', includeStats: boolean = true): Promise<ReportResponse> {
    const response = await apiFetch(`${this.baseUrl}/api/v1/reports/generate-report`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        team_name: teamName,
        report_type: reportType,
        include_stats: includeStats,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to generate report');
    }

    return response.json();
  }

  async getAnalysisTypes(): Promise<any> {
    const response = await apiFetch(`${this.baseUrl}/api/v1/reports/analysis-types`);
    
    if (!response.ok) {
      throw new Error('Failed to get analysis types');
    }

    return response.json();
  }

  async quickAnalysis(type: string): Promise<ApiResponse> {
    const response = await apiFetch(`${this.baseUrl}/api/v1/reports/quick-analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ type }),
    });

    if (!response.ok) {
      throw new Error('Failed to perform quick analysis');
    }

    return response.json();
  }

}

export const apiService = new ApiService();

// Export chatService for backward compatibility
export const chatService = {
  sendMessage: (chatInput: ChatInput): Promise<ChatResponse> => apiService.sendChatMessage(chatInput),
  clearChat: () => apiService.clearChat(),
  generateReport: () => apiService.generateReport('', 'team', true),
  quickAnalysis: (type: string) => apiService.quickAnalysis(type),
};

export default apiService; 
